import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, glyphs, useMediaQuery } from "../../../Styles";
import { ProgressSteps, QuickEntryModalLvl1, QuickEntryModalLvl2, QuickEntryModalLvl3, NewModal, MainContentPaddingTypes } from "../..";
import { QuickEntry, quickEntryChange } from "../../../Definitions";
import { MaintainAnimal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/maintainAnimalInfoCall";
import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { useAnimal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal";
import { NewButton } from "../../BaseComponents/";

interface ReportModal_Props {
  isModalOpen: boolean
  closeModal: () => void,
}

export default function ReportModal({ isModalOpen, closeModal }: ReportModal_Props) {
  const { t } = useTranslation()
  const [currentQuickEntryLevel, setCurrentQuickEntryLevel] = useState(1);
  const mobileXs = useMediaQuery(`(max-width: 793px)`);
  const [quickEntryData, setQuickEntryData] = useState<QuickEntry[]>([]);
  const [quickEntryChange, setQuickEntryChange] = useState<quickEntryChange[]>([]);
  const dirtRef = useRef<MaintainAnimal[]>([]);
  const [error, setError] = useState<string | null>(null)

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  const { maintainEntries: updateAnimals } = useAnimal(connection);

  // Function to handle Quick Entry Modal based on level
  const ShowQuickEntry = (level: 0 | 1 | 2 | 3) => {
    setCurrentQuickEntryLevel(level);
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setCurrentQuickEntryLevel(1); // Reset to Main Info Modal
    }
  }, [isModalOpen]);

  // Function to handle "Go Back"
  const onGoBack = () => {
    if (currentQuickEntryLevel === 3) {
      ShowQuickEntry(2); // Go back to Level 2
    } else if (currentQuickEntryLevel === 2) {
      ShowQuickEntry(1); // Go back to Level 1
    }
  }

  const secondaryContentScroll = useMemo(() => {
    if (currentQuickEntryLevel === 3) return false;
    else return true
  }, [currentQuickEntryLevel])


  return (
    <NewModal
      modalState={isModalOpen}
      overrides={{
        mainContentPadding: (currentQuickEntryLevel === 3) ? undefined : MainContentPaddingTypes.secondary,
        headerContentHeight: "65px",
        secondaryContentScroll: secondaryContentScroll
      }}
      predefinedButtons={{
        onClose: () => { closeModal(); setQuickEntryChange([]); setQuickEntryData([]); dirtRef.current = [] },
        onGoBack: currentQuickEntryLevel === 2 || currentQuickEntryLevel === 3 ? onGoBack : undefined
      }}
      header={t("animalPages.animals.quickEntry", { defaultValue: "quick entry" })}
      headerContent={
        <ProgressSteps
          titles={[t("animalPages.animals.selectAnimals", { defaultValue: "select animals" }), t("animalPages.animals.action", { defaultValue: "action" }), t("animalPages.animals.viewChanges", { defaultValue: "view changes" })]}
          icons={[glyphs.Animal({}), glyphs.Animal({}), glyphs.Animal({})]} opacityValues={currentQuickEntryLevel === 1 ? [1, 0.5, 0.5] : currentQuickEntryLevel === 2 ? [0.5, 1, 0.5] : [0.5, 0.5, 1]}
        />
      }
      secondaryContent={currentQuickEntryLevel === 3 ? <QuickEntryModalLvl3 quickEntryData={quickEntryData} quickEntryChange={quickEntryChange} dirtyDataRef={dirtRef} /> :
        <div style={{ width: mobileXs ? "290px" : "605px" }}>
          {currentQuickEntryLevel === 1 && <QuickEntryModalLvl1 quickEntryData={quickEntryData} onQuickEntryDataChange={setQuickEntryData} />} {/* Select Modal */}
          {currentQuickEntryLevel === 2 && <QuickEntryModalLvl2 quickEntryChange={quickEntryChange} quickEntryData={quickEntryData} onQuickEntryChange={setQuickEntryChange} />} {/* Action Modal */}
        </div>
      }
      footerContent={
        <div style={{ marginTop: currentQuickEntryLevel === 3 ? "20px" : undefined }}>
          <NewButton
            content={(currentQuickEntryLevel === 3) ? t("buttons.apply", { defaultValue: "apply" }) : t("buttons.next", { defaultValue: "next" })}
            onClick={() => currentQuickEntryLevel === 3 ? (() => {
              if (updateAnimals) updateAnimals({ data: dirtRef.current });
              dirtRef.current = [];
              setQuickEntryChange([]);
              setQuickEntryData([]);
              closeModal();
            })()
              : (currentQuickEntryLevel === 1 && (quickEntryData.length === 0 || quickEntryData.some(q => q.value.length === 0))) ? (() => {
                setError("missing select");
                setTimeout(() => setError(null), 2000);
                return;
              })()
                : (currentQuickEntryLevel === 2 && (quickEntryChange.length === 0 || quickEntryChange.some(q => q.value === undefined))) ? (() => {
                  setError("missing action");
                  setTimeout(() => setError(null), 2000);
                  return;
                })()
                  : setCurrentQuickEntryLevel(currentQuickEntryLevel + 1)
            }
          />
        </div>
      }
      footerMessage={error ?? ""}
    />
  );
}
